import React, { useEffect, useState } from "react";
import { Users, User, Camera, X, Expand } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, MaskLine } from "../../lib/motion.jsx";
import { getTeamProfiles, getStaff } from "../../lib/api";

const LOGO_FC = "/inkad-logo.png";
const CATS = [
  { key: "jeugd", label: "catJeugd" },
  { key: "senioren", label: "catSenioren" },
  { key: "futsal", label: "catFutsal" },
  { key: "veteranen", label: "catVeteranen" },
];

const StaffCard = ({ person, t }) => (
  <div className="group flex items-center gap-5 overflow-hidden rounded-md border border-white/10 bg-white/[0.03] p-4 transition-colors duration-300 hover:border-[#CCFF00]/40 sm:p-5" data-testid={`staff-card-${person.id}`}>
    <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-md bg-[#111] ring-2 ring-[#CCFF00]/50 sm:h-28 sm:w-28">
      {person.photo ? (
        <img src={person.photo} alt={person.name} className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" data-testid={`staff-photo-${person.id}`} />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-white/40"><User size={28} /></span>
      )}
    </div>
    <div>
      <span className="block text-[10px] uppercase tracking-[0.25em] text-[#CCFF00]" data-testid={`staff-role-${person.id}`}>{person.role_key ? t(person.role_key) : person.role}</span>
      <h3 className="mt-1 font-anton text-2xl uppercase leading-none text-white" data-testid={`staff-name-${person.id}`}>{person.name}</h3>
    </div>
  </div>
);

const TeamCard = ({ team, t, onOpen }) => (
  <div className="group overflow-hidden rounded-md border border-white/10 bg-white/[0.03] transition-colors duration-300 hover:border-[#CCFF00]/40" data-testid={`team-card-${team.id}`}>
    <div className="relative aspect-[16/9] overflow-hidden bg-[#111]">
      {team.team_photo ? (
        <button
          type="button"
          onClick={() => onOpen(team)}
          aria-label={`Vergroot ploegfoto ${team.name}`}
          data-testid={`team-photo-btn-${team.id}`}
          className="block h-full w-full cursor-zoom-in"
        >
          <img src={team.team_photo} alt={`Ploegfoto ${team.name}`} data-testid={`team-photo-${team.id}`} style={team.team_photo_position ? { objectPosition: team.team_photo_position } : undefined} className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${team.team_photo_contain ? "object-contain" : "object-cover"}`} />
          <span className="pointer-events-none absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100"><Expand size={15} /></span>
        </button>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2" data-testid={`team-photo-placeholder-${team.id}`}>
          <img src={LOGO_FC} alt="" className="h-14 w-auto opacity-25" />
          <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/30"><Camera size={13} /> {t("teamPhotoSoon")}</span>
        </div>
      )}
      {team.name.length <= 10 && (
        <span className="absolute left-3 top-3 rounded-sm bg-[#CCFF00] px-2.5 py-1 text-xs font-extrabold uppercase text-black">{team.name}</span>
      )}
    </div>
    <div className="p-5">
      <h3 className="font-anton text-xl uppercase text-white">{team.name}</h3>
      <p className="mt-2 text-sm text-white/60">{team.description}</p>
      <div className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4">
        {team.coach_photo ? (
          <div
            role="img"
            aria-label={`Coach ${team.coach_name}`}
            data-testid={`team-coach-photo-${team.id}`}
            className="h-11 w-11 rounded-full bg-white/10 bg-no-repeat ring-2 ring-[#CCFF00]/60"
            style={{
              backgroundImage: `url(${team.coach_photo})`,
              backgroundSize: team.coach_zoom || "cover",
              backgroundPosition: team.coach_focus || "center",
            }}
          />
        ) : (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white/40 ring-2 ring-white/10"><User size={20} /></span>
        )}
        <div>
          <span className="block text-[10px] uppercase tracking-[0.25em] text-[#CCFF00]">{t("coachLabel")}</span>
          <span className="block text-sm font-bold text-white" data-testid={`team-coach-${team.id}`}>{team.coach_name || t("coachSoon")}</span>
        </div>
      </div>
    </div>
  </div>
);

export default function FcPloegen() {
  const { t } = useLang();
  const [teams, setTeams] = useState([]);
  const [staff, setStaff] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    getTeamProfiles("fc-inkad").then(setTeams).catch(() => {});
    getStaff("fc-inkad").then(setStaff).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="fc-ploegen-page">
      <div className="mx-auto max-w-7xl px-6 pt-14 pb-6">
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#CCFF00] mb-3">
          <MaskLine delay={0.05}>{t("ploegenKicker")}</MaskLine>
        </div>
        <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
          <MaskLine delay={0.15}>{t("ploegenTitle")}</MaskLine>
        </h1>
        <p className="mt-4 max-w-xl text-sm text-white/60">{t("ploegenLead")}</p>
      </div>

      <div className="mx-auto max-w-7xl px-6 pb-10 space-y-16">
        {staff.length > 0 && (
          <Reveal>
            <section data-testid="staff-section">
              <h2 className="font-anton text-3xl uppercase mb-6 flex items-center gap-3">
                <Users size={24} className="text-[#CCFF00]" /> {t("stafTitle")}
              </h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {staff.map((person) => <StaffCard key={person.id} person={person} t={t} />)}
              </div>
            </section>
          </Reveal>
        )}
        {CATS.map((cat) => {
          const list = teams.filter((tm) => tm.category === cat.key);
          if (!list.length) return null;
          return (
            <Reveal key={cat.key}>
              <section data-testid={`teams-section-${cat.key}`}>
                <h2 className="font-anton text-3xl uppercase mb-6 flex items-center gap-3">
                  <Users size={24} className="text-[#CCFF00]" /> {t(cat.label)}
                </h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((team) => <TeamCard key={team.id} team={team} t={t} onOpen={setLightbox} />)}
                </div>
              </section>
            </Reveal>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm animate-in fade-in"
          onClick={() => setLightbox(null)}
          data-testid="team-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Sluiten"
            data-testid="team-lightbox-close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X size={22} />
          </button>
          <figure className="max-h-[90vh] max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.team_photo} alt={`Ploegfoto ${lightbox.name}`} className="max-h-[82vh] w-auto rounded-md object-contain shadow-2xl" data-testid="team-lightbox-img" />
            <figcaption className="mt-3 text-center font-anton text-xl uppercase tracking-wide text-white">{lightbox.name}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
